"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HistoryStore = void 0;
const tslib_1 = require("tslib");
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const millisecondOperations_1 = require("../../util/time/millisecondOperations");
const enumerableDecorator_1 = require("../constructs/enumerableDecorator");
const CMVarAggregation_1 = require("./CMVarAggregation");
const lambda_1 = require("../../util/lambda");
const number_1 = require("../../util/number");
const debug = lambda_1.DEBUG_OVERRIDE || (lambda_1.NOT_LAMBDA && false);
const debugDateFormat = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };
/**
 * Class Time series data storage object with methods for manipulation

 * Note undefined values held in arrays must be null not undefined to corretly marshall / unmarshall in document client
*/
class HistoryStore {
    /**
     * @param zombie
     */
    constructor(zombie) {
        this.protectedKeys = ['timestamp', 'index', 'systemProperties', 'protectedKeys', 'statistics'];
        this.isProtectedKey = (k) => this.protectedKeys.includes(k);
        /** indexes of ascending ordered oout of order timesamps */
        this.getEqualOrOutOfOrderTimestamps = () => {
            return this.timestamp.reduce((a, c, i, arr) => {
                const next = arr[i + 1];
                if (next) {
                    if (c > next) {
                        a.greater.push(i);
                    }
                    else if (c === next) {
                        a.equal.push(i);
                    }
                }
                return a;
            }, { equal: [], greater: [] });
        };
        /** diff in ms of out of order timestamps where first is greater than next */
        this.getOutOfOrderTimestampsDiff = () => {
            const { greater } = this.getEqualOrOutOfOrderTimestamps();
            return greater.map((gti, i) => this.timestamp[gti] - this.timestamp[i]);
        };
        this.getArrayLength = () => Object.fromEntries(['timestamp', 'index', ...this.keys()].map((k) => [k, this[k].length]));
        /** index is to be deprecated as is superfluous */
        this.index = [];
        this.timestamp = [];
        this.initializeSystemProperties();
        if (zombie) {
            const { statistics, ...others } = zombie;
            Object.assign(this, others);
            this.statistics = new CMVarAggregation_1.CMVarAggregationMap(statistics);
        }
    }
    initializeSystemProperties() {
        if (!this.systemProperties)
            this.systemProperties = {
                autoTrim: false,
                maxAgeMS: undefined,
                maxLength: undefined,
            };
    }
    setAutoTrim(autoTrim) {
        this.initializeSystemProperties();
        this.systemProperties && (this.systemProperties.autoTrim = autoTrim);
    }
    setMaxLength(maxLength) {
        this.initializeSystemProperties();
        this.systemProperties && (this.systemProperties.maxLength = maxLength);
    }
    getMaxLength() {
        return this.systemProperties?.maxLength;
    }
    setMaxAge(maxAgeMS) {
        this.initializeSystemProperties();
        this.systemProperties && (this.systemProperties.maxAgeMS = maxAgeMS);
    }
    getMaxLAge() {
        return this.systemProperties?.maxAgeMS;
    }
    /** Cuts earlier elements off the object history until reaching the maxLength allowed */
    trimToLength() {
        if (this.systemProperties?.maxLength) {
            let shifts = 0;
            while (this.getLength() > this.systemProperties.maxLength) {
                this.shift();
                shifts++;
            }
            // debug && console.log(`with systemProperties.maxLength ${this.systemProperties.maxLength} shifted ${shifts} to become length ${this.getLength() }`)
        }
    }
    trimToAgeMS(maxAge) {
        const maxAgeMs = (0, millisecondOperations_1.timestampToMS)(maxAge);
        const doTrim = () => {
            const oldestTimestamp = (0, millisecondOperations_1.timestampToMS)(this.timestamp[0]);
            return oldestTimestamp && maxAgeMs && oldestTimestamp < maxAgeMs;
        };
        let shifts = 0;
        while (doTrim()) {
            const shiftedSnapshot = this.shift();
            // debug && console.log('shifted', shiftedSnapshot)
            shifts++;
        }
        //debug && console.log(`with maxAge ${ maxAgeMs ? new Date(maxAgeMs) : 'undefined'} shifted ${shifts} to have oldest ${ new Date( this.timestamp[0] )  }, length now ${this.getLength()}`)
    }
    keys() {
        return Object.keys(this).filter((key) => this[key] instanceof Array && !this.isProtectedKey(key));
    }
    hasKey(key) {
        return this.keys().includes(key);
    }
    toZombie() {
        let zombie = {};
        Object.assign(zombie, this);
        // Object.keys(this).filter( ( key ) => ![ 'timestamp', 'index' ].includes(key))
        return zombie;
    }
    getLength() {
        return this.timestamp.length;
    }
    getLastIndex() {
        return this.timestamp.length - 1;
    }
    getLastTimestamp() {
        return this.timestamp[this.getLastIndex()];
    }
    getFirstTimestamp() {
        return this.timestamp[0];
    }
    autoTrimFn() {
        if (this.systemProperties?.autoTrim) {
            if (this.systemProperties?.maxLength) {
                // debug && console.log('Trimming to length',  this.systemProperties.maxLength)
                this.trimToLength();
            }
            if (this.systemProperties?.maxAgeMS) {
                const newestTs = this.timestamp[this.timestamp.length - 1];
                const newestTsMS = (0, millisecondOperations_1.timestampToMS)(newestTs);
                if (newestTsMS) {
                    //  debug && console.log('Trimming to ', newestTsMS - this.systemProperties.maxAgeMS)
                    this.trimToAgeMS(newestTsMS - this.systemProperties.maxAgeMS);
                }
            }
        }
    }
    filter(historyStoreFilter) {
        let slicedStore = undefined;
        if (historyStoreFilter.lastInNMs) {
            debug && console.log(`Filtering to last ${historyStoreFilter.lastInNMs} ms`);
            const lastMs = this.getLastTimestamp();
            if (lastMs) {
                const startMs = lastMs - historyStoreFilter.lastInNMs;
                slicedStore = this.sliceByTimestamp([startMs, lastMs]);
            }
        }
        else if (historyStoreFilter.lastN) {
            debug && console.log(`Filtering to last ${historyStoreFilter.lastN} entries`);
            const lastIdx = this.getLastIndex();
            const startIdx = Math.max(0, lastIdx - historyStoreFilter.lastN);
            slicedStore = this.slice([startIdx, lastIdx + 1]);
            debug && console.log('Sliced length', slicedStore.getLength());
        }
        else if (historyStoreFilter.period) {
            const { fromDateMs, toDateMs } = historyStoreFilter.period;
            slicedStore = this.sliceByTimestamp([fromDateMs, toDateMs]);
            debug && console.log(`Requested between ${(0, millisecondOperations_1.getNiceDate)(fromDateMs)} to ${(0, millisecondOperations_1.getNiceDate)(toDateMs)}`);
            debug && console.log('Sliced length', slicedStore.getLength());
            debug &&
                console.log(`Sliced from ${(0, millisecondOperations_1.getNiceDate)(slicedStore.timestamp[0] ?? 0, debugDateFormat)}`, `to ${(0, millisecondOperations_1.getNiceDate)(slicedStore.getLastTimestamp() ?? 0, debugDateFormat)} `);
            debug &&
                console.log(`Original range from ${(0, millisecondOperations_1.getNiceDate)(this.timestamp[0], debugDateFormat)}`, `to ${(0, millisecondOperations_1.getNiceDate)(this.getLastTimestamp() ?? 0, debugDateFormat)} `);
        }
        if (slicedStore && historyStoreFilter.sampleRate) {
            debug && console.log(`Sparsifying to at sample rate ${historyStoreFilter.sampleRate}`);
            slicedStore = this.diceStore(slicedStore, historyStoreFilter.sampleRate);
        }
        return slicedStore ?? this.clone();
    }
    /**
     * Function to thin data in store by sampling
     */
    diceStore(historyStore, sampleRate) {
        const { statistics, systemProperties } = historyStore;
        const dicedStore = new HistoryStore({ statistics, systemProperties });
        dicedStore.resetStatistics();
        const increment = Math.max(Math.floor(sampleRate), 1);
        const max = historyStore.getLastIndex();
        for (let i = 0; i <= max; i += increment) {
            const stateSnapshot = historyStore.getStateSnapshotByIndex(i);
            if (stateSnapshot) {
                dicedStore.push(stateSnapshot);
            }
            if (i % 1000 === 0) {
                debug && console.log(`length ${historyStore.getLength()} snapshot at ${i}`, stateSnapshot);
            }
        }
        return dicedStore;
    }
    push(snapshot, update = true) {
        debug && console.log('History Store push', snapshot);
        const snapShotKeys = Object.keys(snapshot.state);
        this.index.push(this.getLength());
        this.timestamp.push(snapshot.timestamp);
        const unsetKeys = this.keys().filter((k) => !snapShotKeys.includes(k));
        unsetKeys.forEach((k) => this.pushKV(k, null));
        Object.entries(snapshot.state).forEach(([k, v]) => !this.isProtectedKey(k) && this.pushKV(k, v));
        if (update) {
            this.updateStatistics(snapshot);
        }
        this.autoTrimFn();
        return this;
    }
    unshift(snapshot) {
        const snapShotKeys = Object.keys(snapshot.state);
        this.index.push(this.getLength());
        this.timestamp.unshift(snapshot.timestamp);
        const unsetKeys = this.keys().filter((k) => !snapShotKeys.includes(k));
        unsetKeys.forEach((k) => this.unshiftKV(k, null));
        Object.entries(snapshot.state).forEach(([k, v]) => !this.isProtectedKey(k) && this.unshiftKV(k, v));
        this.updateStatistics(snapshot);
        this.autoTrimFn();
        return this;
    }
    pop() {
        let stateSnapshot = undefined;
        if (this.getLength() > 0) {
            this.index.pop();
            stateSnapshot = { timestamp: 0, state: {} };
            stateSnapshot.timestamp = this.timestamp.pop();
            this.keys().forEach((k) => {
                if (stateSnapshot)
                    stateSnapshot.state[k] = this[k].pop();
            });
        }
        return stateSnapshot;
    }
    shift() {
        let stateSnapshot = undefined;
        if (this.getLength() > 0) {
            this.index.pop();
            stateSnapshot = { timestamp: 0, state: {} };
            stateSnapshot.timestamp = this.timestamp.shift();
            this.keys().forEach((k) => {
                if (stateSnapshot && typeof this[k].shift === 'function') {
                    stateSnapshot.state[k] = this[k].shift();
                }
                else {
                    debug && console.log('Probably not an array', k);
                    debug && console.log('this.keys ', this.keys());
                }
            });
        }
        return stateSnapshot;
    }
    /** returns a copy of the store sliced */
    slice(range, opt = '') {
        debug && console.log('Sliceing by index', opt, range);
        let historySlice = new HistoryStore();
        historySlice.index = this.index.slice(0, range[1] - range[0]);
        historySlice.timestamp = this.timestamp.slice(range[0], range[1]);
        this.keys().forEach((k) => {
            historySlice[k] = this[k].slice(range[0], range[1]);
        });
        return historySlice;
    }
    peek(idx, k) {
        return this[k]?.[idx] ?? null;
    }
    peekRange([start, end], k) {
        return this[k]?.slice(start, end) ?? null;
    }
    peekLastN(n, k) {
        const start = Math.max(this.getLastIndex() - n, 0);
        return this[k]?.slice(start) ?? null;
    }
    peekLast(k) {
        const idx = this.getLastIndex();
        return this[k]?.[idx] ?? null;
    }
    clone() {
        const clone = this.slice([0, this.getLength()]);
        return clone;
    }
    mergeLast(snapshot) {
        const lastState = this.pop();
        if (lastState?.state) {
            Object.entries(snapshot.state).forEach(([k, v]) => {
                if (v !== null && v) {
                    lastState.state[k] = v;
                }
            });
            this.push(lastState);
        }
    }
    updateStatistics(snapshot) {
        if (this.statistics instanceof CMVarAggregation_1.CMVarAggregationMap) {
            this.statistics.update(snapshot);
        }
    }
    resetStatistics(snapshot) {
        this.statistics = new CMVarAggregation_1.CMVarAggregationMap();
        if (snapshot) {
            this.statistics.update(snapshot);
        }
    }
    getStatistics() {
        const stats = {};
        if (this.statistics?.data) {
            const stData = this.statistics.data;
            this.keys().forEach((k) => {
                const st = stData[k];
                if (st) {
                    stats[k] = {
                        firstTimestamp: st.firstTimestamp,
                        lastTimeStamp: st.lastTimeStamp,
                        count: st.cn,
                        mean: st.mean,
                        sdPop: st.sdPop,
                        sdSample: st.sdSample,
                        varPop: st.variancePop,
                        varSample: st.varianceSample,
                        max: st.max,
                        min: st.min,
                    };
                }
            });
        }
        return stats;
    }
    getTimestampRange(timestampRange) {
        const first = Math.max(this.getIndexLastLessThanTimestamp(timestampRange[0]), 0); // Index less than timestamp can be -1 if provided time is before first timestamp
        const latest = this.getIndexLastLessThanTimestamp(timestampRange[1] + 1); // Because it's strictly less than, we gotta add one to get timestamp that are right on the last stamp
        debug && console.log(`Index less than ${new Date(timestampRange[0])}: `, first);
        debug && console.log(`Index less than ${new Date(timestampRange[1])}: `, latest);
        return [first, latest];
    }
    sliceByTimestamp(timestampRange) {
        debug && console.log('Time stamp range to slice by', timestampRange);
        const timeRange = this.getTimestampRange(timestampRange);
        // Because slice is non-inclusive with the last element, we need to increase the end of the range by one to get an accurate slice
        // This is in addition to the addition in getTimestampRange, but both are needed
        const realTimeRange = [timeRange[0], timeRange[1] + 1];
        return this.slice(realTimeRange, 'huh');
    }
    /** returns spliced historyStore */
    splice(startIdx, deleteCount, inserts) {
        const firstSlice = this.slice([0, startIdx]);
        const secondStateSnapshots = this.slice([startIdx + deleteCount, this.getLength()]).getStateSnapshotArray();
        if (inserts) {
            inserts.forEach((snapshop) => firstSlice.push(snapshop));
        }
        secondStateSnapshots.forEach((snapshop) => firstSlice.push(snapshop));
        return firstSlice;
    }
    // we can't seem to mutate in place here so for now have to return this
    insertSnapshot(snapshot) {
        const lastLessIndex = this.getIndexLastLessThanTimestamp(snapshot.timestamp);
        debug && console.log(`HistoryStore insertSnapshot, lastLessIndex ${lastLessIndex}, for timestam ${new Date(snapshot.timestamp).toString()}`);
        if (lastLessIndex >= -1 && lastLessIndex < this.getLength()) {
            // new index zero if below range, max index plus one if out of range or after lastlessIndex, if above range index is length so proxy for push
            const newIndex = lastLessIndex < 0 ? 0 : lastLessIndex === this.getLastIndex() ? this.getLength() : lastLessIndex;
            debug && console.log('Next lowest ', { lastLessIndex, newIndex, currentMax: this.getLastIndex(), state: snapshot.state });
            this.spliceOneUnprotected(newIndex, snapshot);
        }
        else {
            debug && console.log(`HistoryStore insertSnapshot, not inserting, lastLessIndex ${lastLessIndex}, store length ${this.getLength()}`);
        }
        return this;
    }
    /** Inserts a snapshot at start **/
    spliceOneUnprotected(start, snapshot) {
        const snapShotKeys = Object.keys(snapshot.state);
        this.index.push(this.getLength());
        this.timestamp.splice(start, 0, snapshot.timestamp);
        const unsetKeys = this.keys().filter((k) => !snapShotKeys.includes(k));
        unsetKeys.forEach((k) => this[k].splice(start, 0, null));
        Object.entries(snapshot.state).forEach(([k, v]) => {
            this.spliceKV(k, v, start);
        });
    }
    spliceKV(k, v, start) {
        if (!this[k]) {
            this.backFill(k);
            debug && console.log('Added', k);
            debug && console.log('This is', this);
        }
        this[k].splice(start, 0, v);
        //debug && console.log('This k', this[k])
    }
    pushKV(key, value) {
        if (!this.hasKey(key)) {
            this.backFill(key);
        }
        this[key].push(value ?? null);
        return { [key]: value };
    }
    unshiftKV(key, value) {
        if (!this.hasKey(key)) {
            this.backFill(key);
        }
        this[key].unshift(value ?? null);
        return { [key]: value };
    }
    /** if key doesnt exist, adds array for key - (NOTE) with one less cell than current length - which is then pushed, shifted or spliced into */
    backFill(key) {
        if (!this.hasKey(key)) {
            this[key] = new Array(this.getLength() - 1).fill(null);
        }
    }
    addKey(key) {
        if (!this.isProtectedKey(key)) {
            this[key] = new Array(this.getLength()).fill(null);
        }
    }
    removeKey(key) {
        if (!this.isProtectedKey(key)) {
            delete this[key];
        }
    }
    lastTimestamp() {
        const lastIndex = this.getLastIndex();
        return this.timestamp[lastIndex];
    }
    static fromStateSnapshotArray(snapshorArray) {
        const historyStore = new HistoryStore();
        do {
            const snapshot = snapshorArray.shift();
            if (snapshot)
                historyStore.push(snapshot);
        } while (snapshorArray.length > 0);
        return historyStore;
    }
    getIndexLastLessThanTimestampNaiive(t) {
        const i = this.timestamp.findIndex((ti) => ti > t);
        return Math.max(-1, i - 1);
    }
    /** Gets the index (or bucket timestamp) of the most recent non-null value for each reading before provided timestamp
     * @param timestamp - The timestamp to get the most-recent-reading too
     * @param getIndexesOrTimestamps - If indexes, returns the index of the most recent non-null value. If timestamps, map is instead readinKey: timestamp. Useful when using with pushKeywise
     * @returns An object of key -> index (or timestamp) of most recent non-null key value before timestamp
     */
    getIndexLastNonNullKeyLessThanTimestamp(timestamp, getIndexesOrTimestamps = 'indexes') {
        const latestTsIdx = this.getIndexLastLessThanTimestamp(timestamp);
        let latestNonNullTimestamps = {};
        this.keys().forEach((key) => {
            latestNonNullTimestamps[key] = null;
            /** Traverse timestamps backwards, lookng for non-null value of reading */
            if (latestTsIdx === -1)
                return;
            for (let idx = latestTsIdx; idx >= 0; idx--) {
                if (this[key][idx] !== null) {
                    latestNonNullTimestamps[key] = idx;
                    break;
                }
            }
        });
        /** Transform idx -> timestamp if desired */
        if (getIndexesOrTimestamps == 'timestamps') {
            Object.keys(latestNonNullTimestamps).forEach((readingKey) => {
                if (latestNonNullTimestamps[readingKey] != null)
                    latestNonNullTimestamps[readingKey] = this.timestamp[latestNonNullTimestamps[readingKey]];
            });
        }
        return latestNonNullTimestamps;
    }
    /** Runs a binary search through timestamps
     * Gets latest index less than or equal to timestamp
     * */
    /** Gets the index of the latest time less than provided timestamp
     * @param t - Timestamp to find less than
     * @returns The latest index before the provided timestamp or -1 if there is no such timestamp (the provided timestamp is before the earliest index)
     * TODO: return type being -1 is not great because in many cases -1 is a valid index. I think this should return undefined if there is no such index.
     */
    getIndexLastLessThanTimestamp(t) {
        if (this.timestamp.length === 0)
            return -1;
        let i;
        const testSegment = (first, last, timestamp) => {
            const middle = Math.floor((last + first) / 2);
            if (Math.abs(last - first) <= 1) {
                return this.timestamp[first] <= timestamp ? first : last;
            }
            else if (this.timestamp[middle] > timestamp) {
                return testSegment(first, middle, timestamp);
            }
            else {
                return testSegment(middle, last, timestamp);
            }
        };
        const lessThanRange = t <= this.timestamp[0];
        const moreThanRange = t > this.timestamp[this.timestamp.length - 1];
        if (moreThanRange) {
            debug && console.log('More than range ', { t, last: this.timestamp[this.timestamp.length - 1] });
            i = this.timestamp.length - 1;
        }
        else if (lessThanRange) {
            debug && console.log('less than (or equal to) range ', { t, first: this.timestamp[0], diff: this.timestamp[0] - t });
            //i = -1;
            return -1;
        }
        else {
            i = testSegment(0, this.getLastIndex(), t);
        }
        let res = Math.max(0, Math.min(i, this.getLastIndex()));
        debug && console.log('Timestamp', this.timestamp);
        debug && console.log(`Latest timestamp before ${(0, millisecondOperations_1.getNiceDate)(t)} (${t}) is ${(0, millisecondOperations_1.getNiceDate)(this.timestamp[res])} (${this.timestamp[res]}) [${res}]`);
        return Math.max(0, Math.min(i, this.getLastIndex()));
    }
    /** Gets the index of a timestamp within a millisecond range of a provided timestamp.
     * @param timestamp - The timestamp to find the index of a tiemstamp within
     * @param threshold - The milliseconds either side of timestamp that deem stored timestamps valid
     * @param favours - Decides how to favour timestamps if more than one within range is found, or if only one kind is sought
     *    - before - If a timestamp before the provided timestamp within threshold milliseconds is found, return the index of it. Otherwise undefined
     *    - after - If a timestamp after the provided timestamp within threshold milliseconds is found, return the index of it. Otherwise undefined
     *    - closest - If a valid timestamp before and after is found, return the closest one. Favours before on ties
     * */
    getIndexOfTimestampWithin(timestamp, threshold, favour = 'closest') {
        if (this.timestamp.length === 0) {
            return undefined;
        }
        const indexOfTsBefore = this.getIndexLastLessThanTimestamp(timestamp);
        const indexOfTsAfter = indexOfTsBefore + 1; // Theoretically this MUST be after, since if it was before timestamp we would have found it as our indexOfTsBefore
        const timestampBefore = indexOfTsBefore === -1 ? undefined : this.timestamp[indexOfTsBefore];
        // Timestamp after supplied but before threshold
        const timestampAfter = indexOfTsAfter === this.getLastIndex() + 1 ? undefined : this.timestamp[indexOfTsAfter];
        debug && console.log({ timestamp, favour, timestampBefore, timestampAfter });
        if (timestampBefore === timestamp) {
            // I think this might never be true given how getIndexLastLessThanTimestamp works
            return indexOfTsBefore;
        }
        else if (timestampAfter === timestamp) {
            return indexOfTsAfter;
        }
        // Get distances to times
        const distToBefore = Math.abs(timestamp - (timestampBefore ?? Infinity));
        const distToAfter = Math.abs((timestampAfter ?? Infinity) - timestamp);
        // If favoured isn't within range or doesn't exist, return undefined
        if (favour === 'before') {
            return distToBefore > threshold || timestampBefore === undefined ? undefined : indexOfTsBefore;
        }
        else if (favour === 'after') {
            return distToAfter > threshold || timestampAfter === undefined ? undefined : indexOfTsAfter;
        }
        // Closest
        // On ties, favour before. Pretty sure this also catches the cast when one is present and the other isnt
        if (distToBefore <= threshold && distToBefore <= (distToAfter ?? Infinity)) {
            return indexOfTsBefore;
        }
        else if (distToAfter <= threshold && distToAfter <= (distToBefore ?? Infinity)) {
            return indexOfTsAfter;
        }
        else {
            return undefined;
        }
        // Neither timestamp exists
        return undefined;
    }
    getStateSnapshotArray(sort) {
        const clone = this.slice([0, this.getLength()]);
        const snapshotArray = [];
        do {
            const snapShot = clone.shift();
            if (snapShot)
                snapshotArray.push(snapShot);
        } while (clone.getLength() > 0);
        if (sort) {
            const sortFn = (sn1, sn2) => {
                return sort * (sn1.timestamp - sn2.timestamp);
            };
            snapshotArray.sort(sortFn);
        }
        return snapshotArray;
    }
    sort(order) {
        if (this.getLength() > 1000)
            debug && console.log('Warning: Performance will be sub optimal on massive History Stores');
        const res = this.getStateSnapshotArray(order);
        debug && console.log('Snampshot res', res);
        Object.assign(this, HistoryStore.fromStateSnapshotArray(res));
    }
    isTimeOrdered(sort) {
        const limit = this.getLastIndex();
        const isSorted = this.timestamp.every((t, i) => (i < limit ? sort * (t - this.timestamp[i + 1]) <= 0 : true));
        if (isSorted === false) {
            debug &&
                console.log('out of order at ', this.timestamp.findIndex((t, i) => sort * (t - this.timestamp[i + 1]) > 0));
            debug && console.log('lastIndex was', limit);
        }
        return isSorted;
    }
    hasEqualLengthArrays(includeIndex = false) {
        const lArr = this.keys().map((k) => this[k].length);
        if (includeIndex) {
            /** index in deprecation */
            lArr.push(this.index.length);
        }
        lArr.push(this.timestamp.length);
        return lArr.every((l) => l === this.getLength());
    }
    validate(sortOrder = iotv_v3_types_1.SortOrder.ASCENDING) {
        return this.hasEqualLengthArrays() && this.isTimeOrdered(sortOrder);
    }
    validateBounds({ start, end }) {
        const startAfter = (this.getFirstTimestamp() ?? 0) > start;
        const endBefore = (this.getLastTimestamp() ?? 0) < end;
        return startAfter && endBefore;
    }
    getStateSnapshotByIndex(idx) {
        let stateSnapshot = undefined;
        if (idx > -1 && idx < this.getLength()) {
            stateSnapshot = {
                timestamp: this.timestamp[idx],
                state: {},
            };
            for (let k of this.keys()) {
                stateSnapshot.state[k] = this[k][idx];
            }
        }
        return stateSnapshot;
    }
    /** Simple wrapper for getStateSnapshotByindex that lets you use negative numbers to index from the back */
    getStateSnapshotAt(idx) {
        let desiredIdx = idx < 0 ? this.getLength() + idx : idx;
        return this.getStateSnapshotByIndex(desiredIdx);
    }
    generateStatistics() {
        const hasStatistics = Object.keys(this).filter((key) => key === 'statistics');
        if (hasStatistics.length === 0) {
            this.statistics = new CMVarAggregation_1.CMVarAggregationMap();
        }
        const indxArr = this.timestamp;
        const stateSnapshotArr = indxArr.map((_t, ind) => this.getStateSnapshotByIndex(ind));
        stateSnapshotArr.map((stateSnapshot) => stateSnapshot && this.updateStatistics(stateSnapshot));
        return this.getStatistics();
    }
    getLastNullValueIndex(k, beforeIdx) {
        let idx = null;
        if (this.keys().includes(k)) {
            const arr = this[k];
            for (let i = (beforeIdx ?? arr.length) - 1; i >= 0; i--) {
                if (arr[i] === null) {
                    idx = i;
                    break;
                }
            }
        }
        return idx;
    }
    getNextNonNullValueIndex(k, index) {
        let idx = null;
        if (this.keys().includes(k)) {
            const arr = this[k];
            for (let i = index; i < arr.length; i++) {
                if (arr[i] !== null) {
                    idx = i;
                    break;
                }
            }
        }
        return idx;
    }
    getPreviousNonNullValueIndex(k, index) {
        let idx = null;
        if (this.keys().includes(k)) {
            const arr = this[k];
            for (let i = index; i >= 0; i--) {
                if (arr[i] !== null) {
                    idx = i;
                    break;
                }
            }
        }
        return idx;
    }
    inferMissingValues(startVal, endVal, startEndInclusiveTimestamps) {
        const startTimestamp = startEndInclusiveTimestamps.shift();
        const endTimestamp = startEndInclusiveTimestamps.pop();
        let res = null;
        if ((0, number_1.isNumber)(startVal) && (0, number_1.isNumber)(endVal) && (0, number_1.isNumber)(startTimestamp) && (0, number_1.isNumber)(endTimestamp) && startTimestamp < endTimestamp) {
            res = Object.fromEntries(startEndInclusiveTimestamps.map((ts) => {
                const diff = endVal - startVal;
                const periodTotal = endTimestamp - startTimestamp;
                const period = ts - startTimestamp;
                const inferredVal = (period / periodTotal) * diff + startVal;
                return [ts, inferredVal];
            }));
            debug && console.log('missingVals', res);
            return res;
        }
        else {
            debug && console.log('failed predicate', { startTimestamp, endTimestamp, startVal, endVal });
        }
    }
    insertMissingValues(k) {
        if (this.keys().includes(k)) {
            let lastNullIndex = this.getLastNullValueIndex(k);
            let tempLimit = 0;
            while (lastNullIndex && tempLimit < 1000) {
                const previousNonNullIdx = this.getPreviousNonNullValueIndex(k, lastNullIndex);
                const nextNonNullIdx = this.getNextNonNullValueIndex(k, lastNullIndex);
                debug && console.log({ previousNonNullIdx, lastNullIndex, nextNonNullIdx });
                if ((0, number_1.isNumber)(nextNonNullIdx)) {
                    if ((0, number_1.isNumber)(previousNonNullIdx)) {
                        const startVal = this[k][previousNonNullIdx];
                        const endVal = this[k][nextNonNullIdx];
                        const missingValues = this.inferMissingValues(startVal, endVal, this.timestamp.slice(previousNonNullIdx, nextNonNullIdx + 1));
                        debug && console.log({ lastNullIndex, missingValues });
                        if (missingValues) {
                            Object.entries(missingValues).forEach(([ts, val], i) => {
                                const insertIdx = previousNonNullIdx + 1 + i;
                                const correspondingTs = this.timestamp[insertIdx];
                                if (correspondingTs !== parseInt(ts)) {
                                    debug && console.log('WARN: mismatch timestamps', { correspondingTs, ts, val });
                                }
                                else {
                                    this[k][insertIdx] = val;
                                }
                            });
                        }
                    }
                }
                lastNullIndex = this.getLastNullValueIndex(k, lastNullIndex);
                tempLimit++;
            }
        }
    }
    /** returns the total span in milliseconds */
    getTimespan() {
        const firstTimestamp = this.getFirstTimestamp();
        const lastTimeStamp = this.getLastTimestamp();
        return (lastTimeStamp ?? 0) - (firstTimestamp ?? 0);
    }
    /*
     * 2 Feb 2026 ADH - I wrote this function but then didn't need it. Didn't wanna waste it though so I'm leaving it here.
     *
    /** If inserting at a timestamp that already exists, values will be added to buckets that already exist. If the timestamp doesn't exist, the snapshot is inserted like normal
    @enumerable(false)
    additiveInsert(stateSnapshot: StateSnapshot<any>) {
      // TODO: replace with binary search
      const tsIdx = this.timestamp.indexOf(stateSnapshot.timestamp)
  
      if (tsIdx === -1) {
        this.insertSnapshot(stateSnapshot)
        return
      }
  
      for (const key of Object.keys(stateSnapshot.state)) {
        console.log(key, this[key][tsIdx])
        if (!this.hasKey(key)) {
          this.backFill(key)
        }
        if (stateSnapshot.state[key] !== undefined) {
          if (this[key][tsIdx] === null) {
            this[key][tsIdx] = 0
          }
          this[key][tsIdx] += stateSnapshot.state[key] ?? 0
        }
      }
  
      this.updateStatistics(stateSnapshot)
    }
    */
    /** If a state snapshot with an existing timestamp exists, the keys present in state will overwrite the values in the history store. Otherwise the snapshot is inserted.
     * TODO: what do we do about stats.
     * */
    replaceOrInsert(stateSnapshot) {
        // TODO: replace with binary search
        const tsIdx = this.timestamp.indexOf(stateSnapshot.timestamp);
        if (tsIdx === -1) {
            this.insertSnapshot(stateSnapshot);
            return;
        }
        for (const key of Object.keys(stateSnapshot.state)) {
            //console.log(key, this[key][tsIdx])
            if (!this.hasKey(key)) {
                this.backFill(key);
            }
            if (stateSnapshot.state[key] !== undefined) {
                // if (this[key][tsIdx] === null) {
                //   this[key][tsIdx] = 0
                // }
                //console.log('Incoming', key, stateSnapshot.state[key])
                this[key][tsIdx] = stateSnapshot.state[key];
            }
        }
        // This is gonna be totes wrong! Cos we might be replacing data.
        // this.updateStatistics(stateSnapshot)
    }
}
exports.HistoryStore = HistoryStore;
HistoryStore.append = (historyStoresU) => {
    const historyStores = historyStoresU.filter((x) => x !== undefined);
    historyStores.sort((s1, s2) => (s1.getLastTimestamp() ?? 0) - (s2.getLastTimestamp() ?? 0));
    const slicedZombies = historyStores.map((store, i) => {
        const laterStore = historyStores[i + 1];
        const cutoffTimestamp = laterStore?.getFirstTimestamp() ?? 0;
        const storeLastIdxLessThanTimestamp = Math.max(store.getIndexLastLessThanTimestamp(cutoffTimestamp), 0);
        const slice = cutoffTimestamp ? store.slice([0, storeLastIdxLessThanTimestamp]) : store;
        return slice;
    });
    const emptyHistory = new HistoryStore();
    const merged = slicedZombies.reduce((acc, curr) => {
        acc.index = acc.index.concat(curr.index);
        acc.timestamp = acc.timestamp.concat(curr.timestamp);
        acc.systemProperties = {
            autoTrim: false,
            maxAgeMS: undefined,
            maxLength: undefined,
        };
        if (acc.statistics) {
            debug && console.log(`Zeroing statitistics on append`);
            acc.statistics = new CMVarAggregation_1.CMVarAggregationMap({
                data: curr.statistics?.data ?? {},
            });
        }
        Object.entries(curr)
            .filter(([k, v]) => curr.keys().includes(k))
            .forEach(([k, v]) => {
            if (!acc[k])
                acc[k] = [];
            if (acc[k] instanceof Array) {
                acc[k] = [...acc[k], ...v];
            }
        });
        debug && console.log('acc timestamp', acc.timestamp);
        return acc;
    }, emptyHistory);
    return merged;
};
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HistoryStore.prototype, "isProtectedKey", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "initializeSystemProperties", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Boolean]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "setAutoTrim", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "setMaxLength", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", Object)
], HistoryStore.prototype, "getMaxLength", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "setMaxAge", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", Object)
], HistoryStore.prototype, "getMaxLAge", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "trimToLength", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "trimToAgeMS", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "keys", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "hasKey", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", Object)
], HistoryStore.prototype, "toZombie", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "getLength", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "getLastIndex", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", Object)
], HistoryStore.prototype, "getLastTimestamp", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", Object)
], HistoryStore.prototype, "getFirstTimestamp", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "autoTrimFn", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", HistoryStore)
], HistoryStore.prototype, "filter", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [HistoryStore, Number]),
    tslib_1.__metadata("design:returntype", HistoryStore)
], HistoryStore.prototype, "diceStore", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, Boolean]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "push", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "unshift", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "pop", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "shift", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Array, String]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "slice", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number, String]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "peek", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Array, String]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "peekRange", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "peekLast", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "clone", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "mergeLast", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "updateStatistics", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "resetStatistics", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "getStatistics", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Array]),
    tslib_1.__metadata("design:returntype", Array)
], HistoryStore.prototype, "getTimestampRange", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Array]),
    tslib_1.__metadata("design:returntype", HistoryStore)
], HistoryStore.prototype, "sliceByTimestamp", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number, Number, Array]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "splice", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "insertSnapshot", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number, Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "spliceOneUnprotected", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object, Number]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "spliceKV", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "pushKV", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "unshiftKV", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "backFill", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "addKey", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "removeKey", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "lastTimestamp", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "getIndexLastLessThanTimestampNaiive", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false)
    /** Gets the index of the latest time less than provided timestamp
     * @param t - Timestamp to find less than
     * @returns The latest index before the provided timestamp or -1 if there is no such timestamp (the provided timestamp is before the earliest index)
     * TODO: return type being -1 is not great because in many cases -1 is a valid index. I think this should return undefined if there is no such index.
     */
    ,
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number]),
    tslib_1.__metadata("design:returntype", Number)
], HistoryStore.prototype, "getIndexLastLessThanTimestamp", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number]),
    tslib_1.__metadata("design:returntype", Array)
], HistoryStore.prototype, "getStateSnapshotArray", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "sort", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "isTimeOrdered", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HistoryStore.prototype, "getEqualOrOutOfOrderTimestamps", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HistoryStore.prototype, "getOutOfOrderTimestampsDiff", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HistoryStore.prototype, "getArrayLength", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Boolean]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "hasEqualLengthArrays", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "validate", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "validateBounds", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number]),
    tslib_1.__metadata("design:returntype", Object)
], HistoryStore.prototype, "getStateSnapshotByIndex", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number]),
    tslib_1.__metadata("design:returntype", Object)
], HistoryStore.prototype, "getStateSnapshotAt", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "generateStatistics", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Number]),
    tslib_1.__metadata("design:returntype", Object)
], HistoryStore.prototype, "getLastNullValueIndex", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Number]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "getNextNonNullValueIndex", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Number]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "getPreviousNonNullValueIndex", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Number, Number, Array]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "inferMissingValues", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "insertMissingValues", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "getTimespan", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], HistoryStore.prototype, "replaceOrInsert", null);
